import React, { useState, useEffect } from 'react'
import { ThemeContext } from '../contexts/ThemeContext'
import type { Theme } from '../contexts/ThemeContext'

interface ThemeProviderProps {
  children: React.ReactNode
}

// Helper function to get system theme preference
const getSystemTheme = (): Theme => {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  return 'light'
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isSystemMode, setIsSystemMode] = useState(() => {
    return !localStorage.getItem('theme')
  })
  
  const [theme, setTheme] = useState<Theme>(() => {
    // Check if there's a saved theme in localStorage
    const savedTheme = localStorage.getItem('theme') as Theme
    if (savedTheme) {
      return savedTheme
    }
    
    // Use system preference as default
    return getSystemTheme()
  })

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme)
    
    // Save to localStorage only if not in system mode
    if (!isSystemMode) {
      localStorage.setItem('theme', theme)
    }
  }, [theme, isSystemMode])

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      // Only update if in system mode
      if (isSystemMode) {
        setTheme(e.matches ? 'dark' : 'light')
      }
    }

    // Add listener for system theme changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange)
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleSystemThemeChange)
    }

    return () => {
      // Cleanup listener
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleSystemThemeChange)
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleSystemThemeChange)
      }
    }
  }, [isSystemMode])

  const toggleTheme = () => {
    setIsSystemMode(false)
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light')
  }

  const setManualTheme = (newTheme: Theme) => {
    setIsSystemMode(false)
    setTheme(newTheme)
  }

  const resetToSystemTheme = () => {
    // Remove saved preference and enter system mode
    localStorage.removeItem('theme')
    setIsSystemMode(true)
    const systemTheme = getSystemTheme()
    setTheme(systemTheme)
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme: setManualTheme, resetToSystemTheme, isSystemTheme: isSystemMode }}>
      {children}
    </ThemeContext.Provider>
  )
}
